import { createSlice } from "@reduxjs/toolkit";

const initialState = {
    themes: [],
    superthemes: [],
    selectedTheme: null,
    kpis: {},
    flatThemes: {},
    flatSubThemes: {},
    flatKpis: {},
    designPrinciples: [],
    allKPIs: [],
    allLayers: [],
};

/**
 * Reduces an array of themes, each of which contains a direct array of KPI objects,
 * into an object keyed by theme id, where each value is an object keyed by KPI id.
 *
 * Resulting structure:
 * {
 *   [themeId]: {
 *     [kpiId]: kpi
 *   }
 * }
 *
 * @param {Array} themes - The array of themes to reduce.
 * @returns {Object} A mapping of theme ids to their KPI maps.
 */
function reduceKpis(themes) {
    return themes.reduce((themesToKpisMap, theme) => {
        const kpisById = (theme.kpis || []).reduce((kpiMap, kpi) => {
            kpiMap[kpi.id] = kpi;
            return kpiMap;
        }, {});
        themesToKpisMap[theme.id] = kpisById;
        return themesToKpisMap;
    }, {});
}

function flattenThemes(themes) {
    return themes.reduce((acc, theme) => {
        acc[theme.id] = theme;
        return acc;
    }, {});
}

function flattenSubThemes(themes) {
    return themes.reduce((acc, theme) => {
        (theme.subthemes || []).forEach((subtheme) => {
            acc[subtheme.id] = subtheme;
        });
        return acc;
    }, {});
}

function flattenKpis(themes) {
    return themes.reduce((kpiMap, theme) => {
        (theme.kpis || []).forEach((kpi) => {
            kpiMap[kpi.id] = kpi;
        });
        return kpiMap;
    }, {});
}

const themeSlice = createSlice({
    name: "themes",
    initialState,
    reducers: {
        setThemes(state, action) {
            state.themes = action.payload;
            state.kpis = reduceKpis(action.payload);
            state.flatThemes = flattenThemes(action.payload);
            state.flatSubThemes = flattenSubThemes(action.payload);
            state.flatKpis = flattenKpis(action.payload);
        },
        setSuperthemes(state, action) {
            state.superthemes = action.payload;
        },
        setDesignPrinciples(state, action) {
            state.designPrinciples = action.payload;
        },
        setKPIs(state, action) {
            state.allKPIs = action.payload;
        },
        setLayers(state, action) {
            state.allLayers = action.payload;
        },
        addTheme(state, action) {
            state.themes = [...state.themes, action.payload];
            state.kpis = reduceKpis(state.themes);
            state.flatThemes = flattenThemes(state.themes);
            state.flatSubThemes = flattenSubThemes(state.themes);
            state.flatKpis = flattenKpis(state.themes);
        },
        removeTheme(state, action) {
            state.themes = state.themes.filter((theme) => theme.id !== action.payload);
        },
        clearThemes(state) {
            state.themes = [];
            state.selectedTheme = null;
            state.superthemes = [];
            state.kpis = {};
        },
        updateTheme(state, action) {
            const nextState = state.themes.map((theme) => {
                if (theme.id === action.payload.id) {
                    return action.payload;
                }
                return theme;
            });
            state.themes = nextState;
            state.selectedTheme = action.payload;
            state.kpis = reduceKpis(nextState);
            state.flatThemes = flattenThemes(nextState);
            state.flatSubThemes = flattenSubThemes(nextState);
            state.flatKpis = flattenKpis(nextState);
        },
        selectTheme(state, action) {
            state.selectedTheme = state.themes.find((theme) => theme.id == action.payload);
        },
    },
});

export const { 
    setThemes, 
    setSuperthemes, 
    addTheme, 
    removeTheme, 
    clearThemes, 
    updateTheme, 
    selectTheme, 
    setDesignPrinciples,
    setKPIs,
    setLayers
} = themeSlice.actions;

export default themeSlice.reducer;
